/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "nesting" }] */
import { AffiliateOperation, AffiliateState } from "../../interfaces/affiliate";
import { verifiedAffiliateEnvironment } from "../../hooks/api/admin/affiliateEnvironment";
import {
  affiliateSettings,
  AffiliateSettings,
  LAUNCH_POLICIES,
  launchPrepared,
} from "./configuration";

export const FULL_FILL_POLICY = LAUNCH_POLICIES.waiver_policy;

export function ActivationPanel({
  data,
  locked,
  run,
}: {
  data: AffiliateState;
  locked: boolean;
  run: (
    operation: AffiliateOperation,
    payload: Record<string, unknown>
  ) => void;
}) {
  const unavailable = locked || !verifiedAffiliateEnvironment(data);
  const inactive = data.enabled === false && data.weekly_cash === false;
  const compactReady =
    !unavailable &&
    inactive &&
    data.capabilities?.compact_state === true &&
    data.program_version === 1 &&
    data.compact_state === false &&
    /^[a-f0-9]{64}$/.test(data.reset_state_digest || "");
  let settings: AffiliateSettings | undefined;
  try {
    settings = affiliateSettings(data);
  } catch {
    /* Incomplete state cannot be replaced safely. */
  }
  const prepared = launchPrepared(data);
  const policyReady =
    !unavailable &&
    inactive &&
    Boolean(settings) &&
    data.program_version === 2 &&
    data.compact_state === true &&
    !prepared;
  let stateMode = "Unavailable";
  if (data.compact_state !== undefined)
    stateMode = data.compact_state ? "Compact" : "Legacy";

  return (
    <section aria-label="Affiliate launch preparation">
      <h2>Prepare cash and points</h2>
      <p>
        Program version: {data.program_version ?? "Unavailable"}. State mode:{" "}
        {stateMode}
        .
      </p>
      <p>
        Disable the program and weekly cash payments before preparation. Compact
        state preserves balances and unpaid obligations. Review the history
        migration before continuing; the backend verifies that history against
        the current state.
      </p>
      <form
        key={data.reset_state_digest}
        onSubmit={(event) => {
          event.preventDefault();
          if (
            !compactReady ||
            !new FormData(event.currentTarget).has("reviewed")
          )
            return;
          run("compact_state", {
            expected_state_digest: data.reset_state_digest,
          });
        }}
      >
        <fieldset disabled={!compactReady}>
          <label>
            I have reviewed the history migration and disabled affiliate
            activity
            <input name="reviewed" type="checkbox" required />
          </label>
          <button type="submit">
            Verify history and activate compact state
          </button>
        </fieldset>
      </form>
      <p>
        Cash commissions and points accrue on eligible fees; no new DAO rewards
        accrue. Historical DAO obligations remain owed. A zero-fee budget covers
        a fill’s entire fee or none of it, and zero-fee volume earns no rewards.
        Preparation also enables expiring command receipts and the reviewed
        affiliate downgrade, termination and payout-hold rules.
      </p>
      <p>
        {prepared
          ? "Cash and points preparation confirmed by the backend."
          : "Complete compact state first, then prepare the program. Enabling affiliates and weekly payments are separate choices below."}
      </p>
      <form
        key={JSON.stringify(settings)}
        onSubmit={(event) => {
          event.preventDefault();
          if (
            !policyReady ||
            !settings ||
            !new FormData(event.currentTarget).has("reviewed")
          )
            return;
          run("configure", { ...settings, ...LAUNCH_POLICIES });
        }}
      >
        <fieldset disabled={!policyReady}>
          <label>
            I have reviewed these cash and points policies
            <input name="reviewed" type="checkbox" required />
          </label>
          <button type="submit">Prepare cash and points program</button>
        </fieldset>
      </form>
    </section>
  );
}
